import { useCallback, useEffect, useState } from 'react';

import { Api } from '../../api/api';
import type { Child } from '../../api/models';
import { TokenStore } from '../../api/tokenStore';

import type { ChildrenState } from './ChildrenState';

/** Mirrors android/…/Screens/Children/ChildrenViewModel.kt. */
export function useChildren(onEnteredChildMode: (child: Child) => void) {
  const [state, setState] = useState<ChildrenState>({
    isLoading: true,
    children: [],
    error: null,
    errorCorrelationId: null,
    switchingTo: null,
  });

  const load = useCallback(async (): Promise<void> => {
    setState((current) => ({ ...current, isLoading: true, error: null, errorCorrelationId: null }));

    const result = await Api.listChildren();

    setState((current) =>
      result.ok
        ? { ...current, isLoading: false, children: result.value }
        : {
            ...current,
            isLoading: false,
            error: result.error.message,
            errorCorrelationId: result.error.correlationId ?? null,
          },
    );
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return {
    state,
    load,

    /**
     * Switches to a child profile.
     *
     * The child token is *added*, never swapped in: switching back must not make the parent sign in again
     * (ADR-0006), and the parent's own token is untouched by the exchange.
     */
    enterChildMode: async (child: Child): Promise<void> => {
      setState((current) => ({ ...current, switchingTo: child.id, error: null, errorCorrelationId: null }));

      const result = await Api.issueChildToken(child.id);

      if (result.ok) {
        TokenStore.writeChildToken(child.id, result.value.accessToken);
        setState((current) => ({ ...current, switchingTo: null }));
        onEnteredChildMode(child);

        return;
      }

      setState((current) => ({
        ...current,
        switchingTo: null,
        error: result.error.message,
        errorCorrelationId: result.error.correlationId ?? null,
      }));
    },
  };
}
